'use client'

import LinkButton from '@/components/router/LinkButton'
import Button from '@/components/ui-other/basic/Button'
import Image from '@/components/ui-other/basic/Image'
import Link from 'next/link'
import { ComponentType, useState, useEffect, useRef } from 'react'
import { ChevronDown, ChevronUp } from 'react-feather'
import checkLogo from 'shared/imageHelpers'
import { gamePath } from 'shared/urlHelpers'
import { Game, GameState } from 'types'
import LivePlayIcon from 'public/img/ci-icons/live-play.svg'
import { ListItemDetail } from '@/components/newSchedule/ListItemDetail'
import { getDateLabel } from './getDateLabel'
import { motion } from 'framer-motion'
import { ScoreBox } from '@/components/newSchedule/ScoreBox'

export const ListItem: ComponentType<{
  game: Game
  isLoading?: boolean
}> = ({ game, isLoading = false }) => {
  const [detailsOpen, setDetailsOpen] = useState<boolean>(false)
  const [prevState, setPrevState] = useState<GameState | null>(null)
  const [stateChanged, setStateChanged] = useState<boolean>(false)
  const [prevHomeGoals, setPrevHomeGoals] = useState<number | null>(null)
  const [prevAwayGoals, setPrevAwayGoals] = useState<number | null>(null)
  const [homeScoreChanged, setHomeScoreChanged] = useState<boolean>(false)
  const [awayScoreChanged, setAwayScoreChanged] = useState<boolean>(false)
  const initialRender = useRef(true)

  useEffect(() => {
    // Skip animation on initial render
    if (initialRender.current) {
      initialRender.current = false
      setPrevState(game.state)
      setPrevHomeGoals(game.homeGoals)
      setPrevAwayGoals(game.awayGoals)
      return
    }

    // Check for state changes
    if (prevState !== game.state) {
      setStateChanged(true)
      setTimeout(() => setStateChanged(false), 1000)
      setPrevState(game.state)
    }

    // Check for score changes
    if (prevHomeGoals !== game.homeGoals) {
      setHomeScoreChanged(true)
      setTimeout(() => setHomeScoreChanged(false), 600)
      setPrevHomeGoals(game.homeGoals)
    }

    if (prevAwayGoals !== game.awayGoals) {
      setAwayScoreChanged(true)
      setTimeout(() => setAwayScoreChanged(false), 600)
      setPrevAwayGoals(game.awayGoals)
    }
  }, [
    game.state,
    game.homeGoals,
    game.awayGoals,
    prevState,
    prevHomeGoals,
    prevAwayGoals
  ])

  const toggleDetailsOpen = () => {
    setDetailsOpen(!detailsOpen)
  }

  return (
    <motion.div
      className={`hidden @xl/schedule:block bg-white rounded-md transition-all scale-100 ${detailsOpen ? 'ring-2 ring-primary shadow-lg relative' : 'shadow-none'
        }`}
      animate={
        stateChanged
          ? {
            scale: [1, 1.03, 1],
            boxShadow: [
              '0px 0px 0px rgba(0,0,0,0)',
              '0px 10px 25px rgba(0,0,0,0.1)',
              '0px 0px 0px rgba(0,0,0,0)'
            ]
          }
          : {}
      }
      transition={{ duration: 0.5 }}
    >
      <div className="grid grid-cols-[6rem_1fr_4rem_1fr_4.75rem] @2xl/schedule:grid-cols-[7rem_1fr_4rem_1fr_4.75rem] align-middle items-center leading-tight gap-x-4 group">
        <Link href={gamePath(game)} className="contents">
          <div className=""></div>
          <div className="col-span-3 break-all text-center text-sm text-muted pt-4 pb-2">
            {game.tournament.name == game.phase.name
              ? game.tournament.name
              : [game.tournament.name, game.phase.name].join(' - ')}
          </div>
          <div className=""></div>
          <div className="pl-4 pb-4 text-sm @2xl/schedule:text-base">
            {getDateLabel(game.startsAt, game.state)}
          </div>
          <div
            className="flex gap-2 items-center pb-4 link group-has-[.link:hover]:text-green text-sm @2xl/schedule:text-base"
            title={game.homeTeam.name}
          >
            <div className="flex-1 line-clamp-2 leading-[1.2] font-bold text-right">
              {game.homeTeam.name}
            </div>

            <Image
              className="w-6 h-6 shrink-0"
              src={checkLogo(game.homeTeam.logo, 'club')}
              alt={`Logo ${game.homeTeam.name}`}
              objectFit="contain"
            />
          </div>
          <div className="flex items-center justify-center pb-4">
            {isLoading ? (
              <div className="w-full h-6 bg-gray-200 rounded-md animate-pulse" />
            ) : (
              <ScoreBox
                className="w-full"
                game={game}
                homeScoreChanged={homeScoreChanged}
                awayScoreChanged={awayScoreChanged}
              />
            )}
          </div>
          <div
            className="flex justify-start gap-2 items-center pb-4"
            title={game.awayTeam.name}
          >
            <Image
              className="w-6 h-6 shrink-0"
              src={checkLogo(game.awayTeam.logo, 'club')}
              alt={`Logo ${game.awayTeam.name}`}
              objectFit="contain"
            />
            <div className="flex-1 line-clamp-2 leading-[1.2] font-bold text-sm @2xl/schedule:text-base">
              {game.awayTeam.name}
            </div>
          </div>
        </Link>
        <div className="pb-4 pr-1">
          <div className="flex w-full justify-end items-center gap-2">
            {game.livestreamUrl && (
              <LinkButton
                className="!m-0"
                buttonClassName="!p-0.5"
                href={game.livestreamUrl}
                openInNewWindow={true}
                iconOnly
              >
                <LivePlayIcon />
                <span className="sr-only">Zum Livestream öffnen</span>
              </LinkButton>
            )}
            <div title={detailsOpen ? 'Details schließen' : 'Details öffnen'}>
              <Button
                className="!m-0"
                onClick={toggleDetailsOpen}
                iconOnly
                color={'white'}
                tooltip={detailsOpen ? 'Details schließen' : 'Details öffnen'}
                ariaLabel={detailsOpen ? 'Details schließen' : 'Details öffnen'}
              >
                {detailsOpen ? <ChevronUp /> : <ChevronDown />}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className={`pb-4 ${detailsOpen ? 'block' : 'hidden'}`}>
        <hr className="text-gray-500 pb-4 mx-4" />
        <ListItemDetail game={game} />
      </div>
    </motion.div>
  )
}
